import { DateTime } from "luxon";
import { AvailableDate, DateRange, TimeInterval } from "../types";

export const isDateRange = (d: AvailableDate): d is DateRange =>
  typeof d === "object" && "startDate" in d;

// Calendar day of a range bound in the offset it was saved with, as local midnight. Reading it in the
// browser's zone instead shifts the day for viewers outside the clinic's timezone (an end-of-day endDate
// rolls into the next day east of the clinic). Local midnight keeps day math correct across DST.
const toCalendarDay = (iso: string): DateTime => DateTime.fromISO(DateTime.fromISO(iso, { setZone: true }).toISODate() ?? "");

export const countAvailableDays = (dates: AvailableDate[]): number =>
  dates.reduce((total, d) => {
    if (typeof d === "string") return total + 1;
    if (isDateRange(d)) {
      const start = toCalendarDay(d.startDate);
      const end = toCalendarDay(d.endDate);
      return total + Math.round(end.diff(start, "days").days) + 1;
    }
    // TimeInterval counts as one day
    return total + 1;
  }, 0);

export const filterPastAvailableDates = (dates: AvailableDate[]): AvailableDate[] => {
  const today = DateTime.now().startOf("day");
  return dates.reduce<AvailableDate[]>((acc, d) => {
    if (typeof d === "string") {
      if (DateTime.fromISO(d).startOf("day") >= today) acc.push(d);
    } else if (isDateRange(d)) {
      const end = toCalendarDay(d.endDate);
      if (end < today) return acc;
      const start = toCalendarDay(d.startDate);
      acc.push(start < today ? { startDate: today.toISO()!, endDate: d.endDate } : d);
    } else {
      if (DateTime.fromISO(d.start).startOf("day") >= today) acc.push(d);
    }
    return acc;
  }, []);
};

export const expandAvailableDates = (dates: AvailableDate[]): Array<string | TimeInterval> => {
  const result: Array<string | TimeInterval> = [];
  for (const d of dates) {
    if (!isDateRange(d)) {
      result.push(d);
      continue;
    }
    const start = toCalendarDay(d.startDate);
    const end = toCalendarDay(d.endDate);
    const days = Math.round(end.diff(start, "days").days);
    for (let i = 0; i <= days; i++) {
      result.push(start.plus({ days: i }).toISO()!);
    }
  }
  return result;
};

export const consolidateAvailableDates = (dates: AvailableDate[]): AvailableDate[] => {
  const strings = dates.filter((d): d is string => typeof d === "string");
  const others = dates.filter((d) => typeof d !== "string");

  const sorted = strings
    .map((s) => DateTime.fromISO(s))
    .filter((d) => d.isValid)
    .sort((a, b) => a.toMillis() - b.toMillis());

  const ranges: AvailableDate[] = [];
  let rangeStart: DateTime | null = null;
  let rangeEnd: DateTime | null = null;

  for (const dt of sorted) {
    if (!rangeStart || !rangeEnd) {
      rangeStart = dt;
      rangeEnd = dt;
    } else if (dt.startOf("day").diff(rangeEnd.startOf("day"), "days").days <= 1) {
      rangeEnd = dt;
    } else {
      ranges.push(
        rangeStart.startOf("day").toISODate() === rangeEnd.startOf("day").toISODate()
          ? rangeStart.toISO()!
          : { startDate: rangeStart.toISO()!, endDate: rangeEnd.endOf("day").toISO()! },
      );
      rangeStart = dt;
      rangeEnd = dt;
    }
  }

  if (rangeStart && rangeEnd) {
    ranges.push(
      rangeStart.startOf("day").toISODate() === rangeEnd.startOf("day").toISODate()
        ? rangeStart.toISO()!
        : { startDate: rangeStart.toISO()!, endDate: rangeEnd.endOf("day").toISO()! },
    );
  }

  return [...ranges, ...others];
};
